// Query-key factories for the agents families carved at ARCFE-1051.
//
// Frozen verbatim from their pre-move definition sites — `inboxQueryKeys` from
// `src/features/inbox/hooks.ts` and `AGENT_MEMORIES_QUERY_KEY` from
// `src/features/projects/agent-memory/queryKeys.ts`. Both old paths are now
// re-export shims, so every cache identity in the app is byte-identical and
// there is exactly ONE definition site per key (packages/CLAUDE.md).
//
// NAME COLLISION, DELIBERATELY PRESERVED: `inboxQueryKeys` is also the name of
// an unrelated factory in `src/features/document-inbox/hooks/queryKeys.ts`,
// which is INTEGRATIONS-owned and roots at `['document-inbox']`. The 4.1
// classification (ARCFE-1040) recorded the root-level collision; this is the
// symbol-level form of the same thing. They are different keys in different
// domains and both keep their names: the agents one moves here, the
// integrations one is untouched. Do not "deduplicate" them.
import type { InboxItemsParams } from './inbox.types';
import type { ProjectListQuery } from './projects/requests.types';
import type { RefillListQuery } from './refills.types';
import type { WorkflowListQuery } from './routines.types';

export const inboxQueryKeys = {
  all: ['inbox'] as const,
  items: (params?: InboxItemsParams) => ['inbox', 'items', params] as const,
};

export const AGENT_MEMORIES_QUERY_KEY = 'agent-memories';

export const GET_STRUCTURED_PRECHART = 'structured-prechart';

export const WORK_CHAT_APPROVAL_MODE_QUERY_KEY = 'getWorkChatApprovalModeSetting';

export const SESSION_SURFACE_ITEMS_QUERY_KEY = 'session-surface-items';

export const sessionSurfaceItemsQueryKey = (sessionId: string) =>
  [SESSION_SURFACE_ITEMS_QUERY_KEY, { sessionId }] as const;

export const WORK_CHAT_RUN_SPEED_QUERY_KEY = 'getWorkChatRunSpeedSetting';

// ---- Harness family (ARCFE-1059) -------------------------------------------
// Frozen verbatim from `src/features/tool-approvals/queryKeys.ts` and
// `src/features/agent-harness/hooks.ts`. `hooks.ts` stays app-side — it holds
// blockers this milestone does not heal — so it re-exports `agentHarnessKeys`
// from here rather than declaring it, keeping one definition site per key.

export const toolApprovalQueryKeys = {
  all: ['tool-approvals'] as const,
  pending: (agentRunId: string | null | undefined) =>
    [...toolApprovalQueryKeys.all, 'pending', agentRunId] as const,
};

export const agentHarnessKeys = {
  all: ['agent-harness'] as const,
  run: (agentRunId: string) => [...agentHarnessKeys.all, 'run', agentRunId] as const,
};

// ---- Routines family (ARCFE-1102) ------------------------------------------
// Frozen verbatim from `src/features/workflows/queryKeys.ts`, which is now a
// named re-export shim. The tuples are characterized in `keys.test.ts` rather
// than merely moved: `runs` BRANCHES on `limit === undefined` and `allRuns`
// substitutes the literal `'all'` for an absent id, so those two are cache
// identities a re-typing could shift without any test noticing.
//
// The pre-move file typed `list`'s argument with a SECOND, local copy of
// `WorkflowListQuery`; it now imports the single declaration from
// `routines.types.ts` (see that file's note).
//
// NOT IN THIS FACTORY, and left exactly as found: three key prefixes the app
// spells inline rather than through `WORKFLOW_KEYS` —
// `['workflows', 'schedules-batch', ...ids]`, the `[...templates, 'connector', k]`
// suffix, and the bare `['workflows', 'list']` / `['workflows', 'all-runs']`
// invalidation prefixes. Adding them here would be a cache-identity change
// wearing a tidy-up's clothes; they are `agents/routines`' first `query`-slice
// work, recorded on the family rather than done in a contracts move.
export const WORKFLOW_KEYS = {
  all: ['workflows'] as const,
  templates: ['workflows', 'templates'] as const,
  // The preset catalog is server-localised, so the active locale is part of
  // the cache identity. `templates` stays the invalidation prefix.
  templatesForLocale: (locale: string) =>
    [...WORKFLOW_KEYS.templates, { locale }] as const,
  list: (query: WorkflowListQuery = {}) => ['workflows', 'list', query] as const,
  detail: (id: string) => ['workflows', 'detail', id] as const,
  schedule: (id: string) => ['workflows', 'schedule', id] as const,
  eventRules: (id: string) => ['workflows', 'event-rules', id] as const,
  runs: (id: string, limit?: number) =>
    limit === undefined
      ? (['workflows', 'runs', id] as const)
      : (['workflows', 'runs', id, { limit }] as const),
  allRuns: (workflowId?: string) =>
    ['workflows', 'all-runs', workflowId ?? 'all'] as const,
  sessionRuns: (sessionId: string) => ['workflows', 'session-runs', sessionId] as const,
};

// The routine document pickers' list identities. Bare strings, frozen verbatim
// from the picker module; `useWorkflowContextDocumentUpload` wraps the first in
// a one-element tuple to invalidate it.
export const WORKFLOW_CONTEXT_DOCS_QUERY_KEY = 'workflow-context-documents';
export const LIBRARY_ARTEFACT_DOCS_QUERY_KEY = 'library-artefact-documents';

// ---- Refills family (AGE-2309) ---------------------------------------------
// Born here rather than moved: the pre-pagination surface read one unbounded
// list under `GET_REPEAT_REVIEWS` (`src/hooks/query/useQueryRepeatReviews.ts`,
// still the legacy Repeats tab's identity and untouched). These are new
// identities for the paginated list and the full-queue summary, rooted so the
// decision mutation can evict both with one prefix.
export const refillReviewKeys = {
  all: ['refill-reviews'] as const,
  pages: (query?: RefillListQuery) => ['refill-reviews', 'pages', query] as const,
  summary: () => ['refill-reviews', 'summary'] as const,
  // The single review with its chart snapshot (AGE-2794). Under the same
  // prefix, so a decision's `all` invalidation refreshes it too.
  detail: (id: string) => ['refill-reviews', 'detail', id] as const,
};

// ---- Concierge family (AGE-2758) -------------------------------------------
export const conciergeRequestKeys = {
  all: ['concierge-requests'] as const,
};

// ---- Projects family -------------------------------------------------------
// Frozen verbatim from `src/features/projects/hooks.ts`, where it was `KEYS`; the
// module is now a binding over the package hooks and declares no key.
//
// The list key carries the query params so two callers requesting different
// shapes (e.g. the sidebar at ``limit=6`` and the landing page at
// ``limit=50, q=foo``) don't trample each other's cache.
export const projectsQueryKeys = {
  list: (query: ProjectListQuery) => ['projects', 'list', query] as const,
  detail: (id: string) => ['projects', 'detail', id] as const,
  // Keyed by the reply id so each assistant turn gets its own
  // (cached) set of follow-ups rather than reusing the first turn's;
  // language is part of the key so a locale switch refetches.
  followUp: (id: string, replyId: string, displayLanguage?: string) =>
    ['projects', 'follow-up', id, replyId, displayLanguage ?? null] as const,
};

// ---- Work experience feedback ------------------------------------------------
// A PERSISTED key: the localStorage slot that records a submitted Work feedback
// nudge. The e2e suppression helper writes the same literal to keep the nudge
// out of specs, and the app's suppression-keys pin holds the two together.
export const WORK_EXPERIENCE_FEEDBACK_SUBMITTED_KEY =
  'heidi:work-experience-feedback-submitted';
